import { DecorationSet } from '@codemirror/view';
import { Maximize01Icon, Minimize01Icon } from '@langchain/untitled-ui-icons';

import { useMemo, useState } from 'react';

import { ExpandCollapseProvider } from '@/Pages/Graph/src/components/debug/common/expandCollapseContext';
import { ForeignDataTreeView } from '@/Pages/Graph/src/components/debug/common/foreign-data-tree';
import {
  RenderModeContext,
  useRenderModeCtx,
} from '@/Pages/Graph/src/control/state';
import {
  foreignDataToTree,
  parseForeignData,
} from '@/Pages/Graph/src/data/foreign-data';
import { CodeData } from '@/components/Code/Code';
import Header from '@/components/CodeHeader';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { IFrameTraceRenderer } from '@/components/IFrameTraceRenderer';
import { Message } from '@/components/Message';
import { FancyRenderingProvider } from '@/components/RichTextEditor/utils/fancyRenderContext';
import { useFancyRendering } from '@/components/RichTextEditor/utils/fancyRenderContext/useFancyRender';
import { useIFrameTraceRendering } from '@/hooks/useIFrameTraceRendering';
import { OrgConfigs, useOrgConfig } from '@/hooks/useOrgConfig';
import {
  ExampleSchema,
  MessageFields,
  RunInputSchema,
  RunOutputSchema,
  RunSchema,
} from '@/types/schema';
import { cn } from '@/utils/tailwind';
import { useLocalStorageState } from '@/utils/use-local-storage-state';

import { ErrorBoundaryRawFallback } from '../../../components/ErrorBoundaryRawFallback';
import { RunDisplaySchema } from '../utils/types';
import { Skeleton } from './InputOutputLoadingSkeleton';
import { RunDetailsFunctionsAndStructuredOutput } from './RunDetailsFunctions';
import { RunDetailsHeaderToggle } from './RunDetailsHeaderToggle';
import { RunDetailsPlain } from './RunDetailsPlain';
import { RunInput } from './RunInput';
import { RunOutput } from './RunOutput';

interface RunDetailsProps {
  run: RunDisplaySchema;
  prevRun?: RunSchema | undefined;
  variant?:
    | 'run'
    | [
        'plain',
        {
          input:
            | { type: 'key-heuristic' }
            | { type: 'key'; value: string }
            | { type: 'key'; value: string[] };
          output:
            | { type: 'key-heuristic' }
            | { type: 'key'; value: string }
            | { type: 'key'; value: string[] };
        }
      ]
    | 'example';
  exampleOutput?: ExampleSchema['outputs'];
  inputs?: RunInputSchema;
  outputs?: RunOutputSchema;
  forceRawRender?: 'raw' | 'rendered';
  onEditInputs?: (inputs: RunSchema['inputs']) => void;
  onEditOutputs?: (outputs: RunSchema['outputs']) => void;
  getDiffValues?: (type: 'inputs' | 'outputs') =>
    | {
        decoration: DecorationSet;
        generatedCode: string | undefined;
      }
    | undefined;
  inputsRef?: React.RefObject<HTMLDivElement>;
  outputsRef?: React.RefObject<HTMLDivElement>;
  inputSchema?: Record<string, unknown>;
  outputSchema?: Record<string, unknown>;
  inputsLoading?: boolean;
  outputsLoading?: boolean;
  errorsLoading?: boolean;
  showExpandAll?: boolean;
}

export function RunDetailsIO({
  run,
  prevRun,
  variant,
  exampleOutput,
  forceRawRender,
  getDiffValues,
  onEditInputs,
  onEditOutputs,
  inputsRef,
  outputsRef,
  inputSchema,
  outputSchema,
  inputsLoading,
  outputsLoading,
  errorsLoading,
  showExpandAll,
}: RunDetailsProps) {
  const { value: enableMarkdownInTracing } = useOrgConfig(
    OrgConfigs.enable_markdown_in_tracing
  );
  const { inputs, outputs } = run;

  const [_rawInput, setRawInput] = useLocalStorageState(
    'ls:runDetails:rawInput',
    false
  );
  const [_rawOutput, setRawOutput] = useLocalStorageState(
    'ls:runDetails:rawOuptut',
    false
  );
  const { currIFrameConfig } = useIFrameTraceRendering(
    run.reference_dataset_id
  );
  const inputDiffs = useMemo(() => getDiffValues?.('inputs'), [getDiffValues]);
  const outputDiffs = useMemo(
    () => getDiffValues?.('outputs'),
    [getDiffValues]
  );
  const rawInput =
    forceRawRender === 'raw'
      ? true
      : forceRawRender === 'rendered'
      ? false
      : _rawInput;

  const rawOutput =
    forceRawRender === 'raw'
      ? true
      : forceRawRender === 'rendered'
      ? false
      : _rawOutput;

  // Used for collated conversation view and extracting particular keys from inputs/outputs
  if (Array.isArray(variant) && variant[0] === 'plain') {
    return (
      <RunDetailsPlain
        run={run}
        prevRun={prevRun}
        variant={variant}
        inputs={inputs}
        outputs={outputs}
        forceRawRender={forceRawRender}
        onEditInputs={onEditInputs}
        onEditOutputs={onEditOutputs}
        inputsRef={inputsRef}
        outputsRef={outputsRef}
        inputsLoading={inputsLoading}
        outputsLoading={outputsLoading}
        errorsLoading={errorsLoading}
      />
    );
  }

  return (
    <>
      {run.run_type == 'llm' && !onEditInputs && (
        <RunDetailsFunctionsAndStructuredOutput run={run} outputs={outputs} />
      )}
      <div
        className="border-b border-tertiary p-4 last-of-type:border-b-0"
        ref={inputsRef}
        data-testid="run-details-input"
      >
        <FancyRenderingProvider
          enableMarkdownInTracing={!!enableMarkdownInTracing}
        >
          <RunFancyInput
            inputs={inputs}
            inputsLoading={inputsLoading}
            run={run}
            forceRawRender={forceRawRender}
            diffValues={inputDiffs}
            onEditInputs={onEditInputs}
            rawInput={rawInput}
            setRawInput={setRawInput}
            schema={inputSchema}
            showExpandAll={showExpandAll}
          />
        </FancyRenderingProvider>
      </div>
      <FancyRenderingProvider
        enableMarkdownInTracing={!!enableMarkdownInTracing}
      >
        <RunFancyOutput
          outputs={outputs}
          rawOutput={rawOutput}
          setRawOutput={setRawOutput}
          forceRawRender={forceRawRender}
          outputsLoading={outputsLoading}
          run={run}
          diffValues={outputDiffs}
          onEditOutputs={onEditOutputs}
          outputsRef={outputsRef}
          schema={outputSchema}
          showExpandAll={showExpandAll}
          variant={variant === 'example' ? 'reference output' : 'output'}
        />
      </FancyRenderingProvider>
      {exampleOutput && (
        <div className="border-b border-tertiary p-4 last-of-type:border-b-0">
          <Header text="Reference Output" />

          {currIFrameConfig?.enabled && !rawOutput ? (
            <IFrameTraceRenderer
              config={currIFrameConfig}
              payload={exampleOutput}
              type="reference"
              key={`${run.id}-reference`}
            />
          ) : (
            <Message
              content=""
              kwargs={exampleOutput}
              testid="reference-output-component"
            />
          )}
        </div>
      )}
    </>
  );
}

export function RunFancyInput({
  inputs,
  inputsLoading,
  run,
  forceRawRender,
  diffValues,
  onEditInputs,
  rawInput,
  setRawInput,
  hideHeaders,
  schema,
  showExpandAll,
}: {
  inputs: RunInputSchema | undefined;
  inputsLoading?: boolean;
  run: RunDisplaySchema;
  forceRawRender?: 'raw' | 'rendered';
  diffValues?: {
    decoration: DecorationSet;
    generatedCode: string | undefined;
  };
  onEditInputs?: (inputs: RunSchema['inputs']) => void;
  rawInput: boolean;
  setRawInput: (value: boolean) => void;
  hideHeaders?: boolean;
  schema?: Record<string, unknown>;
  showExpandAll?: boolean;
}) {
  const inputsComponent = (
    <>
      {!hideHeaders && (
        <RunDetailsHeaderToggle
          variant="input"
          raw={rawInput}
          readOnly={forceRawRender != null}
          onRawChange={setRawInput}
          disabled={!!diffValues}
          disabledMessage="Turn off diff mode to change input view"
        />
      )}
      {inputsLoading ? (
        <Skeleton />
      ) : rawInput ? (
        <div className="overflow-hidden rounded-lg border border-secondary">
          <CodeData
            value={inputs}
            diffValues={diffValues}
            onChange={onEditInputs}
            readOnly={!onEditInputs}
            schema={schema}
          />
        </div>
      ) : (
        <div>
          <ErrorBoundary
            fallback={(error) => (
              <ErrorBoundaryRawFallback
                data={inputs}
                msg={`Failed to render the input, falling back to raw view: ${error.message}`}
                diffValues={diffValues}
                onChange={onEditInputs}
                readOnly={!onEditInputs}
                schema={schema}
              />
            )}
          >
            <RunInput
              attachments={run.inputs_s3_urls}
              inputs={inputs}
              onEditInputs={onEditInputs}
              runType={run.run_type}
            />
          </ErrorBoundary>
        </div>
      )}
    </>
  );

  return showExpandAll ? (
    <ExpandCollapseProvider
      localStorageKey={`ls:message-schema-editor-expand-collapse-input`}
    >
      {inputsComponent}
    </ExpandCollapseProvider>
  ) : (
    inputsComponent
  );
}

export function RunFancyOutput({
  outputs,
  outputsLoading,
  run,
  forceRawRender,
  diffValues,
  onEditOutputs,
  rawOutput,
  setRawOutput,
  outputsRef,
  hideHeaders,
  className,
  schema,
  showExpandAll,
  variant,
}: {
  outputs: RunOutputSchema | undefined;
  outputsLoading?: boolean;
  run: RunDisplaySchema;
  forceRawRender?: 'raw' | 'rendered';
  diffValues?: {
    decoration: DecorationSet;
    generatedCode: string | undefined;
  };
  onEditOutputs?: (outputs: RunSchema['outputs']) => void;
  rawOutput: boolean;
  setRawOutput: (value: boolean) => void;
  outputsRef?: React.RefObject<HTMLDivElement>;
  hideHeaders?: boolean;
  className?: string;
  schema?: Record<string, unknown>;
  showExpandAll?: boolean;
  variant?: 'output' | 'reference output';
}) {
  const { currIFrameConfig } = useIFrameTraceRendering(
    run.reference_dataset_id
  );

  const outputsComponent = (
    <div
      className={cn(
        'border-b border-tertiary p-4 last-of-type:border-b-0',
        className
      )}
      ref={outputsRef}
      data-testid="run-details-output"
    >
      {!hideHeaders && (
        <RunDetailsHeaderToggle
          variant={variant ?? 'output'}
          raw={rawOutput}
          onRawChange={setRawOutput}
          disabled={!!diffValues}
          customIframe={currIFrameConfig?.enabled}
          readOnly={forceRawRender != null}
          disabledMessage="Turn off diff mode to change output view"
        />
      )}

      {outputsLoading ? (
        <Skeleton />
      ) : currIFrameConfig?.enabled && outputs && !rawOutput ? (
        <IFrameTraceRenderer
          config={currIFrameConfig}
          payload={outputs}
          type="output"
          key={`${run.id}-output`}
        />
      ) : rawOutput ? (
        <div className="overflow-hidden rounded-lg border border-secondary">
          <CodeData
            value={outputs}
            diffValues={diffValues}
            onChange={onEditOutputs}
            schema={schema}
            readOnly={!onEditOutputs}
          />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <ErrorBoundary
            fallback={(error) => (
              <ErrorBoundaryRawFallback
                data={outputs}
                msg={`Failed to render the output, falling back to raw view: ${error.message}`}
                diffValues={diffValues}
                onChange={onEditOutputs}
                readOnly={!onEditOutputs}
                schema={schema}
              />
            )}
          >
            <RunOutput
              attachments={run.outputs_s3_urls}
              outputs={outputs}
              onEditOutputs={onEditOutputs}
              runType={run.run_type}
            />
          </ErrorBoundary>
        </div>
      )}
    </div>
  );

  return showExpandAll ? (
    <ExpandCollapseProvider
      localStorageKey={`ls:message-schema-editor-expand-collapse-output`}
    >
      {outputsComponent}
    </ExpandCollapseProvider>
  ) : (
    outputsComponent
  );
}

export function CollapsibleAdditionalFields({
  kwargs,
  variant,
  attachments,
  onEditKwargs,
  expanded,
}: {
  kwargs: { [key: string]: any };
  variant?: 'output' | 'reference' | 'conversation';
  attachments?: RunSchema['outputs_s3_urls'];
  onEditKwargs?: (value: MessageFields['additional_kwargs']) => void;
  expanded: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState(
    expanded || Boolean(onEditKwargs)
  );
  const fieldCount = Object.keys(kwargs || {}).length;
  const { enableMarkdownInTracing } = useFancyRendering();
  const renderModeState = useRenderModeCtx({
    renderMode: 'pretty',
    enableMarkdown: !!enableMarkdownInTracing,
  });

  return (
    <>
      <div className="flex items-center gap-2 px-4 py-3 text-sm">
        <div className="flex items-center gap-2 font-semibold uppercase text-tertiary">
          <span>Additional Fields</span>
        </div>

        <span className="rounded bg-tertiary px-1 font-medium text-tertiary">
          {fieldCount}
        </span>

        {!onEditKwargs && (
          <button
            type="button"
            className="stroke-current"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? (
              <Minimize01Icon className="h-4 w-4" />
            ) : (
              <Maximize01Icon className="h-4 w-4" />
            )}
          </button>
        )}
      </div>

      {isExpanded &&
        // todo: we shouldn't be render additional fields using the message component
        // but as of writing this, we don't support editing or attachments in the foreign data tree
        (onEditKwargs || attachments ? (
          <Message
            content=""
            variant={variant}
            kwargs={kwargs}
            attachments={attachments}
            onEditKwargs={onEditKwargs}
          />
        ) : (
          <RenderModeContext.Provider value={renderModeState}>
            <div className="px-3 pb-3">
              <ForeignDataTreeView
                data={foreignDataToTree({ data: parseForeignData(kwargs) })}
              />
            </div>
          </RenderModeContext.Provider>
        ))}
    </>
  );
}
